import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import currentTimeZone from '../../../../config/timeZone';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGenericLatest,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';

interface ChartDataProperties {
  label: string;
  scheduleChargeRevenue: number;
  [key: string]: string | number; // Add this to allow dynamic properties
}

export type TimeRange = 'last24hours' | 'last7days' | 'lastMonth' | 'last12months';

/**
 *  Energy report duration chart
 */
function TotalPowerConsumedChart() {
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { endConsumerType, reportFilter } = useAppSelector((state) => state.endConsumerType);

  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;

      return (
        <div className="energy-report-tooltip">
          <p className="label">{data?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#2E9E3A' }}>
              {t('powerConsumed')} : {ToFixedValue(data?.scheduleChargeRevenue || 0)}{' '}
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      setIsLoading(true);
      const response = await endConsumerApi.fetchTotalChartApi({
        timePeriod: range,
        timeZone: currentTimeZone,
        type: endConsumerType,
      });

      if (response.status === 200) {
        let transformedData: ChartDataProperties[];

        switch (range) {
          case 'last12months':
            transformedData = fillMissingMonthsGeneric(response?.data?.results).map((item) => ({
              label: item.label,
              scheduleChargeRevenue: (item as any).totalPowerConsumed || 0,
            }));
            break;
          case 'lastMonth':
            transformedData = fillMissingLastNDaysGenericLatest(response?.data?.results, 30).map(
              (item) => ({
                label: item.label,
                scheduleChargeRevenue: (item as any).totalPowerConsumed || 0,
              }),
            );
            break;
          case 'last7days':
            transformedData = fillMissingLastNDaysGenericLatest(response?.data?.results, 7).map(
              (item) => ({
                label: item.label,
                scheduleChargeRevenue: (item as any).totalPowerConsumed || 0,
              }),
            );
            break;
          case 'last24hours':
            transformedData = fillMissingLast24HrsGeneric(response?.data?.results).map((item) => ({
              label: item.label,
              scheduleChargeRevenue: (item as any).totalPowerConsumed || 0,
            }));
            break;
          default:
            transformedData = [];
        }

        setBarGraphData(transformedData);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (reportFilter.range) {
      fetchPowerConsumedReport(reportFilter.range as TimeRange);
    }
  }, [reportFilter.range, endConsumerType]);

  return (
    <div className="total-revenue-chart-wrap border" dir={i18n.dir()}>
      <div className="total-revenue-chart-header">
        <div className="header-title">{t('totalPowerConsumed')}</div>
      </div>
      {isLoading ? (
        <div className="revenue-consumer-loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <div className="total-revenue-chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barGraphData} barCategoryGap={25}>
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  fontSize={12}
                  stroke="#535353"
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                <Bar dataKey="scheduleChargeRevenue" stackId="a" barSize={26} fill="#F79009" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="revenue-footer">
            <div className="color-border" style={{ background: '#F79009' }} />
            <p className="title">{t('powerConsumed')}</p>
            <p className="value">
              {ToFixedValue(
                barGraphData?.reduce(
                  (accumulator, item) => accumulator + item.scheduleChargeRevenue,
                  0,
                ),
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default TotalPowerConsumedChart;
